import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, CheckCircle, TrendingUp, Lock, Unlock } from 'lucide-react';
import { fetchBotPatternPerformance } from '@/services/pattern-combo-analytics-service';
import type { PatternPerformanceData } from '@/services/pattern-combo-analytics-service';

interface BotPatternPerformanceProps {
  settingId: string;
  botId: string;
  timeRange: string;
}

export const BotPatternPerformance: React.FC<BotPatternPerformanceProps> = ({
  settingId,
  botId,
  timeRange
}) => {
  const { data: patterns = [], isLoading, error } = useQuery({
    queryKey: ['bot-pattern-performance', settingId, botId, timeRange],
    queryFn: () => fetchBotPatternPerformance(settingId, botId, timeRange),
    staleTime: 30 * 1000, // Cache for 30 seconds
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-red-600">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>Không thể tải dữ liệu pattern performance</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (patterns.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Chưa có pattern rules nào được cấu hình</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate summary stats
  const totalLocks = patterns.reduce((sum, p) => sum + p.lock_count, 0);
  const totalOpens = patterns.reduce((sum, p) => sum + p.open_count, 0);
  const totalSkips = patterns.reduce((sum, p) => sum + p.skips, 0);
  const activePatterns = patterns.filter(p => p.lock_count >= 5).length;
  const unusedPatterns = patterns.filter(p => p.lock_count === 0 && p.open_count === 0).length;

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Overall Win Rate Card */}
        {patterns[0]?.overall_win_rate !== undefined && (
          <Card className="md:col-span-2">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-muted-foreground">Backtest Win Rate</p>
                    <Badge variant="secondary" className="text-xs">
                      📊 Simulation
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <p className={`text-3xl font-bold ${
                      patterns[0].overall_win_rate !== null && patterns[0].overall_win_rate >= 60 ? 'text-green-600' : 
                      patterns[0].overall_win_rate !== null && patterns[0].overall_win_rate >= 50 ? 'text-blue-600' : 'text-red-600'
                    }`}>
                      {patterns[0].overall_win_rate !== null ? patterns[0].overall_win_rate.toFixed(2) : '0.00'}%
                    </p>
                    <Badge variant="outline">
                      {patterns[0].overall_wins}W / {patterns[0].overall_losses}L
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {patterns[0].overall_forwards} signals forwarded (simulation)
                  </p>
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    💡 Production có thể khác do virtual forwards/group logic
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Locks</p>
                <p className="text-3xl font-bold mt-1">{totalLocks}</p>
              </div>
              <Lock className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Opens</p>
                <p className="text-3xl font-bold mt-1">{totalOpens}</p>
              </div>
              <Unlock className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Insights */}
      {unusedPatterns > 0 && (
        <Card className="border-orange-200 bg-orange-50/50 dark:bg-orange-950/20">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              Insights & Recommendations
            </CardTitle>
            <CardDescription className="text-xs">
              Dựa trên backtest simulation. Kết quả production có thể khác.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {unusedPatterns > 0 && (
              <p className="text-orange-600">
                • {unusedPatterns} pattern{unusedPatterns > 1 ? 's' : ''} chưa được sử dụng trong khoảng thời gian này.
              </p>
            )}
            {activePatterns < patterns.length / 2 && patterns.length > 2 && (
              <p className="text-orange-600">
                • Tỷ lệ patterns hoạt động thấp. Cân nhắc điều chỉnh priority hoặc tạo combo mới.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pattern Table */}
      <Card>
        <CardHeader>
          <CardTitle>Pattern Rules Performance (Backtest)</CardTitle>
          <CardDescription>
            Pure backtest simulation chạy theo logic GATE/LOCK/OPEN thuần túy trong {timeRange}. 
            Kết quả dùng để phân tích hiệu suất lý thuyết của pattern rules, có thể khác Type 2 Overview (production).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lock</TableHead>
                  <TableHead>Open</TableHead>
                  <TableHead className="text-center">Priority</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Locks</TableHead>
                  <TableHead className="text-right">Opens</TableHead>
                  <TableHead className="text-right">Skips</TableHead>
                  <TableHead className="text-center">Activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {patterns.map((pattern) => {
                  const totalActivity = pattern.lock_count + pattern.open_count;
                  const activityLevel = totalActivity === 0 ? 'None' : 
                                        totalActivity >= 20 ? 'High' : 
                                        totalActivity >= 10 ? 'Medium' : 'Low';
                  const activityColor = activityLevel === 'High' ? 'text-green-600' : 
                                       activityLevel === 'Medium' ? 'text-blue-600' : 
                                       activityLevel === 'Low' ? 'text-orange-600' : 'text-muted-foreground';
                  
                  return (
                    <TableRow key={pattern.id}>
                      <TableCell className="font-mono font-semibold">{pattern.lock_pattern}</TableCell>
                      <TableCell className="font-mono font-semibold">{pattern.open_pattern}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{pattern.priority}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {pattern.enabled ? (
                          <Badge variant="default" className="text-xs">Enabled</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Disabled</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-red-600">
                        {pattern.lock_count}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-green-600">
                        {pattern.open_count}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {pattern.skips}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={activityColor}>
                          {activityLevel}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
